import React, { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Button,
  Collapse,
  ListItem,
  Tooltip,
  makeStyles
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

const useStyles = makeStyles((theme) => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0
  },
  button: {
    color: theme.palette.text.secondary,
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%'
  },
  buttonLeaf: {
    color: theme.palette.text.secondary,
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      '& $title': {
        fontWeight: theme.typography.fontWeightMedium
      }
    }
  },
  buttonCollapsed: {
    justifyContent: 'center',
    padding: '10px 0',
    minWidth: 0,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
  iconCollapsed: {
    marginRight: 0,
  },
  title: {
    marginRight: 'auto'
  },
  active: {
    color: theme.palette.secondary.main,
    '& $title': {
      fontWeight: theme.typography.fontWeightMedium
    },
    '& $icon': {
      color: theme.palette.secondary.main
    }
  }
}));

const NavItem = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  open: openProp,
  title,
  collapsed,
  ...rest
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(openProp);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = { paddingLeft };

  if (children) {
    return (
      <ListItem
        className={clsx(classes.item, className)}
        disableGutters
        key={title}
        {...rest}
      >
        <Button
          className={clsx(classes.button, collapsed && classes.buttonCollapsed)}
          onClick={handleToggle}
          style={collapsed ? {} : style}
        >
          {Icon && (
            <Icon
              className={clsx(classes.icon, collapsed && classes.iconCollapsed)}
              size="20"
            />
          )}
          {!collapsed && <span className={classes.title}>{title}</span>}
          {!collapsed && (open ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
        </Button>
        {!collapsed && (
          <Collapse in={open}>
            {children}
          </Collapse>
        )}
      </ListItem>
    );
  }

  const buttonEl = (
    <Button
      activeClassName={classes.active}
      className={clsx(
        classes.buttonLeaf,
        `depth-${depth}`,
        collapsed && classes.buttonCollapsed
      )}
      component={RouterLink}
      exact
      style={collapsed ? {} : style}
      to={href}
    >
      {Icon && (
        <Icon
          className={clsx(classes.icon, collapsed && classes.iconCollapsed)}
          size="20"
        />
      )}
      {!collapsed && <span className={classes.title}>{title}</span>}
      {!collapsed && Info && <Info />}
    </Button>
  );

  return (
    <ListItem
      className={clsx(classes.itemLeaf, className)}
      disableGutters
      key={title}
      {...rest}
    >
      {collapsed ? (
        <Tooltip title={title} placement="right">
          {buttonEl}
        </Tooltip>
      ) : buttonEl}
    </ListItem>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  collapsed: PropTypes.bool,
};

NavItem.defaultProps = {
  open: false,
  collapsed: false,
};

export default NavItem;
